import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import * as _ from 'lodash-es';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { ComponentDefaultsEditService } from './component-defaults-edit.service';

@Component({
    selector: 'gtw-component-defaults-edit',
    templateUrl: './component-defaults-edit.component.html',
    styleUrls: ['./component-defaults-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ComponentDefaultsEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  GENERAL_CONFIG: any = {};
  private globalParams: any = {};
  filterParams: any = {};
  private userSettings: any = {};

  minimise = false;
  loading = false;
  isSaveClicked = false;
  crudLoading = false;
  lockedRowExist = false;
  logged_in_user = '';
  curentyear = '';
  scenario = '';
  jcd_key: any;
  scenarioDesc: string | null = null;
  QARLocked = 'N';

  defaultsMultipleSelected: any[] = [];
  private gridDataMultipleSelect: any[] = [];
  allStatus: string[] = [];

  // Dropdown data
  sourceData: any[] = [];
  fdiiData: any[] = [];
  countryData: any[] = [];
  prorateYesNoData: any[] = [];
  gpVarianceData: any[] = [];
  sicCodeData: any[] = [];
  yesNoData: any[] = [];
  methodData: any[] = [];

  // Selected values
  selectedSourceCode: any = null;
  selectedFdiiCode: any = null;
  selectedCountryCode: any = null;
  selectedProrateCOGS: any = null;
  selectedgpVariances: any = null;
  selectedSicCode: any = null;
  selectedFSC: any = null;
  selectedFinancialCorp: any = null;
  selectedMethod: any = null;

  // Attribute master data for save payload key mapping
  attribMastData: any[] = [];

  private is_locked: any = null;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar, private gtwSnackbar: GtwSnackbarService,
    private editService: ComponentDefaultsEditService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalParams = header.globalParams || {};
    this.userSettings = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    if (gridPayload) {
      this.gridDataMultipleSelect = _.clone(Array.isArray(gridPayload) ? gridPayload : []);
    }
  }

  private jsonGlobalContext(): any {
    return { globalParams: this.globalParams };
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG?.base_url) return;
    this.isInitialized = true;

    this.logged_in_user = this.userSettings?.user?.sso_id || this.userSettings?.sso_id || '';
    this.curentyear = this.filterParams.tax_year;
    this.scenario = this.filterParams.scenario;
    this.jcd_key = this.globalParams.jcd_key;

    this.checkLockStatus();
    this.processGridData();
    this.getCodeMastData();
    this.getAttribMast();
  }

  private checkLockStatus(): void {
    this.editService.getLockStatus(
      this.GENERAL_CONFIG.base_url, this.scenario, this.jcd_key, this.jsonGlobalContext()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data?.jsonObject) {
        this.is_locked = data.jsonObject;

        if (this.is_locked?.[0]?.SCENARIO_DESC === 'EXAM') {
          this.scenarioDesc = 'EXAM';
        }

        if (this.is_locked?.[0]?.LOCK_STATUS === 'Y') {
          this.cancel();
          this.gtwSnackbar.openSnackBar('Tax Return has already been filed for tax year ' + this.curentyear + '.', 'gtw-snackbar--red', true, 4);
          return;
        }

        if (this.scenarioDesc === 'EXAM' && this.QARLocked === 'Y') {
          this.cancel();
          this.gtwSnackbar.openSnackBar('Updating Component Default is not available for selected Scenario.', 'gtw-snackbar--red', true, 4);
          return;
        }
      }
      this.cdr.detectChanges();
    });
  }

  private processGridData(): void {
    if (!this.gridDataMultipleSelect || this.gridDataMultipleSelect.length === 0) return;

    this.defaultsMultipleSelected = [];
    this.allStatus = [];
    this.lockedRowExist = false;

    for (const item of this.gridDataMultipleSelect) {
      const row = item?.data;
      if (!row) continue;

      this.defaultsMultipleSelected.push(row);
      this.allStatus.push(row.STATUS_FLAG);

      if (row.STATUS_FLAG === 'Locked') {
        this.lockedRowExist = true;
      }

      const hasExisting = row.BASKET_0 != '' || row.FDII_0 != '' || row.COUNTRY_0 != '' ||
        row.PRORATE_COGS_0 != '' || row.GP_VARIANCES_0 != '' || row.SIC_CODES_0 != '' ||
        row.FSE_0 != '' || row.FINANCIAL_CORP_0 != '';

      if (hasExisting) {
        row.operation_type = 'U';
      } else {
        row.operation_type = 'I';
        this.scenarioDesc = row.SCENARIO;
      }

      if (row.operation_type === 'U') {
        this.QARLocked = 'Y';
      }

      if (this.is_locked?.[0]?.SCENARIO_DESC === 'EXAM' && row.operation_type === 'U') {
        this.cancel();
        this.gtwSnackbar.openSnackBar('Updating Component Default is not available for selected Scenario.', 'gtw-snackbar--red', true, 4);
        return;
      }
    }

    // Validate: all must be from same Parent ME Code
    const selectedMECode = this.gridDataMultipleSelect[0]?.data?.PARENT_ME_CODE;
    const otherSelectedMe = _.filter(this.gridDataMultipleSelect, (o: any) =>
      o?.data?.PARENT_ME_CODE !== selectedMECode
    );

    const lockedInfo = _.includes(this.allStatus, 'Locked');
    const unLockedInfo = _.includes(this.allStatus, 'Unlocked');
    const undefinedInfo = _.includes(this.allStatus, undefined);

    if (lockedInfo && (unLockedInfo || undefinedInfo)) {
      this.gtwSnackbar.openSnackBar('Please select Locked or Unlocked or empty', 'gtw-snackbar--red', true, 4);
      this.cancel();
      return;
    }

    if (lockedInfo && this.allStatus.length === this.gridDataMultipleSelect.length) {
      this.gtwSnackbar.openSnackBar('Entity is locked', 'gtw-snackbar--yellow', true, 4);
    }

    if (otherSelectedMe && otherSelectedMe.length > 0) {
      this.gtwSnackbar.openSnackBar('Please select components from same Parent ME Code', 'gtw-snackbar--red', true, 400);
      this.cancel();
      return;
    }

    this.cdr.detectChanges();
  }

  private getCodeMastData(): void {
    if (!this.gridDataMultipleSelect || this.gridDataMultipleSelect.length === 0) return;

    const ccKey = this.gridDataMultipleSelect[0]?.data?.COMBINATION_KEY;
    this.editService.getCodeMastData(
      this.GENERAL_CONFIG.base_url, this.scenario, this.jcd_key, ccKey, this.jsonGlobalContext()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data?.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
        this.gtwSnackbar.openSnackBar(data.errorMessage, 'gtw-snackbar--red', true, 4);
        return;
      }
      const jsonObj = data?.jsonObject || [];

      this.sicCodeData = _.filter(jsonObj, (p: any) => _.includes(['SIC_CODE_PER_ME'], p.CODE_GROUP));
      if (this.sicCodeData.length === 0) {
        this.sicCodeData = _.filter(jsonObj, (p: any) => _.includes(['SIC_CODE'], p.CODE_GROUP));
      }
      this.countryData = _.filter(jsonObj, (p: any) => _.includes(['COUNTRY_CODE'], p.CODE_GROUP));
      this.sourceData = _.filter(jsonObj, (p: any) => _.includes(['SOURCE_CODE'], p.CODE_GROUP));
      this.fdiiData = _.filter(jsonObj, (p: any) => _.includes(['FDII_CODE'], p.CODE_GROUP));
      this.gpVarianceData = _.filter(jsonObj, (p: any) => _.includes(['GP_VARIANCE'], p.CODE_GROUP));
      this.yesNoData = _.filter(jsonObj, (p: any) => _.includes(['YES_NO'], p.CODE_GROUP));
      this.prorateYesNoData = _.filter(jsonObj, (p: any) => _.includes(['YES_NO'], p.CODE_GROUP));
      this.methodData = _.filter(jsonObj, (p: any) => _.includes(['METHOD_CODE_EXPAA'], p.CODE_GROUP));

      if (this.scenarioDesc === 'EXAM') {
        _.remove(this.gpVarianceData, { CODE_DESCRIPTION: 'Prorate' });
        _.remove(this.prorateYesNoData, { CODE_DESCRIPTION: 'Yes' });
      }

      this.cdr.detectChanges();
    });
  }

  private getAttribMast(): void {
    this.editService.getAttribMast(
      this.GENERAL_CONFIG.base_url, this.scenario, this.jcd_key, this.jsonGlobalContext()
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data: any) => {
      if (data?.errorMessage && data.errorMessage.length > 0 && data.errorMessage !== 'null') {
        this.gtwSnackbar.openSnackBar(data.errorMessage, 'gtw-snackbar--red', true, 4);
        return;
      }
      this.attribMastData = data?.jsonObject || [];
      this.cdr.detectChanges();
    });
  }

  removeDefaultsRow(row: any): void {
    const index = this.defaultsMultipleSelected.indexOf(row);
    if (index > -1) {
      this.defaultsMultipleSelected.splice(index, 1);
    }
    this.cdr.detectChanges();
  }

  reset(): void {
    this.selectedSourceCode = null;
    this.selectedFdiiCode = null;
    this.selectedCountryCode = null;
    this.selectedgpVariances = null;
    this.selectedProrateCOGS = null;
    this.selectedFSC = null;
    this.selectedFinancialCorp = null;
    this.selectedSicCode = null;
    this.selectedMethod = null;
    this.cdr.detectChanges();
  }

  save(): void {
    this.isSaveClicked = true;

    if (this.defaultsMultipleSelected.length === 0) {
      this.gtwSnackbar.openSnackBar('Please select atleast one Component Default Mapping', 'gtw-snackbar--red', true, 4);
      this.isSaveClicked = false;
      return;
    }

    const insertExist = _.find(this.defaultsMultipleSelected, ['operation_type', 'I']);

    if (insertExist != undefined) {
      if (this.selectedSourceCode == null || this.selectedFdiiCode == null ||
          this.selectedCountryCode == null || this.selectedgpVariances == null ||
          this.selectedProrateCOGS == null || this.selectedFSC == null ||
          this.selectedFinancialCorp == null || this.selectedSicCode == null ||
          this.selectedMethod == null) {
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Please select all the Defaults', 'gtw-snackbar--red', true, 4);
        return;
      }
    } else {
      if (this.selectedSourceCode == null && this.selectedFdiiCode == null &&
          this.selectedCountryCode == null && this.selectedgpVariances == null &&
          this.selectedProrateCOGS == null && this.selectedFSC == null &&
          this.selectedFinancialCorp == null && this.selectedSicCode == null &&
          this.selectedMethod == null) {
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Please select atleast one Default', 'gtw-snackbar--red', true, 4);
        return;
      }
    }

    const defaultsChangeDetails: any[] = [];

    for (let key = 0; key < this.defaultsMultipleSelected.length; key++) {
      const gridRow = this.gridDataMultipleSelect[key]?.data;
      if (!gridRow) continue;

      const returnObj: any = {};
      returnObj.object_id = gridRow.object_id;
      returnObj.ho_leid = gridRow.HO_LEID;
      returnObj.leid = gridRow.LEID;
      returnObj.cdr_number = gridRow.CDR_NO;
      returnObj.reporting_period = gridRow.REPORTING_PERIOD;
      returnObj.me_code = gridRow.ME_CODE;

      const basketAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'BASKET']);
      if (basketAttrib) {
        returnObj[basketAttrib.ATTRIB_KEY + '_0'] = (this.selectedSourceCode != null) ? this.selectedSourceCode : gridRow.SOURCE_KEY;
      }
      const fdiiAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'FDII']);
      if (fdiiAttrib) {
        returnObj[fdiiAttrib.ATTRIB_KEY + '_0'] = (this.selectedFdiiCode != null) ? this.selectedFdiiCode : gridRow.FDII_KEY;
      }
      const countryAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'COUNTRY']);
      if (countryAttrib) {
        returnObj[countryAttrib.ATTRIB_KEY + '_0'] = (this.selectedCountryCode != null) ? this.selectedCountryCode : gridRow.COUNTRY_KEY;
      }
      const prorateAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'PRORATE_COGS']);
      if (prorateAttrib) {
        returnObj[prorateAttrib.ATTRIB_KEY + '_0'] = (this.selectedProrateCOGS != null) ? this.selectedProrateCOGS : gridRow.COGS_KEY;
      }
      const gpAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'GP_VARIANCES']);
      if (gpAttrib) {
        returnObj[gpAttrib.ATTRIB_KEY + '_0'] = (this.selectedgpVariances != null) ? this.selectedgpVariances : gridRow.GROSS_KEY;
      }
      const sicAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'SIC_CODES']);
      if (sicAttrib) {
        returnObj[sicAttrib.ATTRIB_KEY + '_0'] = (this.selectedSicCode != null) ? this.selectedSicCode : gridRow.SIC_KEY;
      }
      const fseAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'FSE']);
      if (fseAttrib) {
        returnObj[fseAttrib.ATTRIB_KEY + '_0'] = (this.selectedFSC != null) ? this.selectedFSC : gridRow.FSC_KEY;
      }
      const finAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'FINANCIAL_CORP']);
      if (finAttrib) {
        returnObj[finAttrib.ATTRIB_KEY + '_0'] = (this.selectedFinancialCorp != null) ? this.selectedFinancialCorp : gridRow.FINANCIAL_KEY;
      }
      const methodAttrib = _.find(this.attribMastData, ['ATTRIB_NAME', 'METHOD']);
      if (methodAttrib) {
        returnObj[methodAttrib.ATTRIB_KEY + '_0'] = (this.selectedMethod != null) ? this.selectedMethod : gridRow.METHOD;
      }

      returnObj.COMBINATION_KEY = this.defaultsMultipleSelected[key].COMBINATION_KEY;
      returnObj.operation_type = this.defaultsMultipleSelected[key].operation_type;

      defaultsChangeDetails.push(returnObj);
    }

    const returnClobSettingsObj: any = {
      sso_id: this.logged_in_user,
      screen_key: this.globalParams.screen_key,
    };

    this.editService.saveDefaults(
      this.GENERAL_CONFIG.base_url,
      this.globalParams,
      this.filterParams,
      returnClobSettingsObj,
      defaultsChangeDetails
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (result: any) => {
        const response = result?.data || result || {};
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.isSaveClicked = false;
          this.gtwSnackbar.openSnackBar(response.errorMessage, 'gtw-snackbar--red', true, 4);
        } else {
          this.gtwSnackbar.openSnackBar('Defaults has been successfully saved/updated', 'gtw-snackbar--green', true, 4);
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      () => {
        this.isSaveClicked = false;
        this.gtwSnackbar.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red', true, 4);
        this.cdr.detectChanges();
      }
    );
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
